import { Outlet, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useUser } from '../contexts/UserContext';
import { useSystemParameters } from '../hooks/useSystemParameters';
import { useState, useEffect } from 'react';

function Layout() {
  const { accounts } = useMsal();
  const { user, loading, error, logout } = useUser();
  const { getBooleanValue } = useSystemParameters();
  const [bypassTokenValidation, setBypassTokenValidation] = useState<boolean>(false);
  const [bypassUserInfo, setBypassUserInfo] = useState<any>(null);

  // Check for security bypass parameter
  useEffect(() => {
    const checkBypassParameter = async () => {
      const bypassValue = getBooleanValue('security.bypass', false);
      setBypassTokenValidation(bypassValue === true);
      
      if (bypassValue === true && accounts.length > 0) {
        // If bypass is enabled, get user info directly from MSAL account
        const account = accounts[0];
        setBypassUserInfo({
          firstName: account.idTokenClaims?.given_name || account.name?.split(' ')[0] || 'User',
          lastName: account.idTokenClaims?.family_name || account.name?.split(' ')[1] || '',
          email: account.username,
          agency: { name: 'Bypass Mode' },
          roles: [{ name: 'Bypass User' }]
        });
      }
    };

    checkBypassParameter();
  }, [getBooleanValue, accounts]);

  const handleLogout = () => {
    void logout();
  };

  // Determine which user info to display
  const displayUser = bypassTokenValidation ? bypassUserInfo : user;
  const displayLoading = bypassTokenValidation ? false : loading;
  const displayError = bypassTokenValidation ? null : error;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-family)' }}>
      <nav style={{ 
        width: '280px', 
        background: 'white', 
        padding: '24px', 
        borderRight: '1px solid #e9ecef',
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ 
          color: 'var(--secondary-color)', 
          marginBottom: '32px',
          fontSize: '1.75rem',
          fontWeight: '700'
        }}>
          TAP
        </h2>
        
        {/* Clean User Info Section */}
        <div style={{ 
          marginBottom: '24px',
          background: 'white',
          borderRadius: '8px',
          padding: '20px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
        }}>
          {displayLoading ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              color: 'var(--secondary-color)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', opacity: 0.7 }}>hourglass_empty</span>
              </div>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '2px', color: 'var(--secondary-color)' }}>Loading...</div>
                <div style={{ fontSize: '14px', color: '#6c757d' }}>Please wait</div>
              </div>
            </div>
          ) : displayError ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              color: 'var(--primary-color)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#fff5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px'
              }}>
                <span className="material-symbols-outlined error" style={{ fontSize: '20px', opacity: 0.7 }}>warning</span>
              </div>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '2px', color: 'var(--primary-color)' }}>Error</div>
                <div style={{ fontSize: '14px', color: '#6c757d' }}>Failed to load user info</div>
              </div>
            </div>
          ) : displayUser ? (
            <div>
              {/* Bypass Mode Warning */}
              {bypassTokenValidation && (
                <div style={{ 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7', 
                  borderRadius: '6px', 
                  padding: '8px 12px', 
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#856404',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span className="material-symbols-outlined warning" style={{ fontSize: '16px', opacity: 0.7 }}>warning</span>
                  <span>Bypass Mode Active</span>
                </div>
              )}
              
              {/* User Avatar and Info */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'var(--primary-color)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'white',
                  flexShrink: 0,
                  lineHeight: '1'
                }}>
                  {displayUser.firstName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontWeight: '600', 
                    fontSize: '16px',
                    marginBottom: '4px',
                    color: 'var(--secondary-color)',
                    lineHeight: '1.2'
                  }}>
                    {displayUser.firstName} {displayUser.lastName}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#6c757d',
                    lineHeight: '1.3',
                    wordBreak: 'break-word',
                    marginBottom: '8px'
                  }}>
                    {displayUser.email}
                  </div>
                </div>
                {/* Online Status Indicator */}
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: '#28a745',
                  border: '2px solid white',
                  boxShadow: '0 0 0 1px #e9ecef',
                  flexShrink: 0,
                  marginTop: '4px'
                }} />
              </div>
              
              {/* User Details */}
              <div style={{ 
                background: '#f8f9fa', 
                borderRadius: '6px', 
                padding: '12px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', opacity: 0.6, color: '#6c757d' }}>business</span>
                  <span style={{ fontWeight: '500', color: 'var(--secondary-color)' }}>{displayUser.agency.name}</span>
                </div>
                {displayUser.group && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    fontSize: '14px',
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', opacity: 0.6, color: '#6c757d' }}>groups</span>
                    <span style={{ fontWeight: '500', color: 'var(--secondary-color)' }}>
                      {displayUser.group.name}
                    </span>
                  </div>
                )}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontSize: '14px'
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', opacity: 0.6, color: '#6c757d' }}>person</span>
                  <span style={{ fontWeight: '500', color: 'var(--secondary-color)' }}>
                    {displayUser.roles.map((role: any) => role.name).join(', ')}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              color: 'var(--secondary-color)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', opacity: 0.7 }}>help</span>
              </div>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '2px', color: 'var(--secondary-color)' }}>No User Info</div>
                <div style={{ fontSize: '14px', color: '#6c757d' }}>Please refresh the page</div>
              </div>
            </div>
          )}
        </div>

        <ul style={{ 
          listStyle: 'none', 
          padding: 0,
          margin: 0
        }}>
          {(() => {
            const cannotAccessUploadOrReports = !!displayUser?.roles?.some(
              (role: any) =>
                role.id === 'customer-role-id' || role.id === 'customer-admin-role-id',
            );
            return [
            { to: '/', label: 'Dashboard', icon: 'dashboard' },
            { to: '/upload', label: 'Upload Data', icon: 'cloud_upload' },
            { to: '/reports', label: 'Reports', icon: 'assessment' },
            { to: '/users', label: 'Users', icon: 'group', adminOnly: true },
            { to: '/agencies', label: 'Agencies', icon: 'business', adminOnly: true, requireRoleId: 'admin-role-id' },
            { to: '/groups', label: 'Groups', icon: 'groups', adminOnly: true, requireRoleId: 'admin-role-id' },
            { to: '/system-parameters', label: 'System Parameters', icon: 'settings', adminOnly: true, requireRoleId: 'admin-role-id' },
            { to: '/audit', label: 'Audit Trail', icon: 'history', adminOnly: true, requireRoleId: 'admin-role-id' },
            { to: '/user-guide', label: 'User Guide', icon: 'help' }
          ].filter((item) => {
            if (cannotAccessUploadOrReports && (item.to === '/upload' || item.to === '/reports')) {
              return false;
            }
            // Per-item admin-only rules
            if (item.adminOnly) {
              // If this item explicitly requires a role id, enforce it strictly
              if ((item as any).requireRoleId) {
                const requiredId = (item as any).requireRoleId;
                // Allow in bypass mode as before
                const hasBypass = displayUser?.roles?.some((r: any) => r.name === 'Bypass User');
                const hasRequired = displayUser?.roles?.some((r: any) => r.id === requiredId);
                return !!(hasRequired || hasBypass);
              }
              // Default admin visibility: prior behavior (admin or agency admin)
              return displayUser?.roles?.some((role: any) => 
                role.id === 'admin-role-id' ||
                role.id === 'agency-admin-role-id' ||
                role.id === 'group-admin-role-id' ||
                role.id === 'customer-admin-role-id' ||
                role.name === 'Bypass User'
              );
            }
            return true;
          }).map((item) => (
            <li key={item.to} style={{ margin: '8px 0' }}>
              <Link 
                to={item.to} 
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  color: 'var(--secondary-color)',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  fontSize: '15px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--secondary-color)';
                }}
              >
                <span className="material-symbols-outlined" style={{ marginRight: '12px', fontSize: '20px', opacity: 0.7 }}>{item.icon}</span>
                {item.label}
              </Link>
            </li>
          ));
          })()}
        </ul>
        
        <button 
          onClick={handleLogout} 
          style={{ 
            marginTop: '32px',
            width: '100%',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
            background: 'none',
            color: '#dc3545',
            border: '1px solid #dc3545',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', opacity: 0.7 }}>logout</span>
          <span>Logout</span>
        </button>
      </nav>
      
      <main style={{ 
        flex: 1, 
        padding: '32px',
        background: '#f8f9fa',
        minHeight: '100vh'
      }}>
        <Outlet />
      </main>
    </div>
  );
}

export default Layout;


